import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  Button,
  Box
} from "@mui/material";

import {
  getIntegration,
  getIntegrationOptions,
  updateIntegrationOptions,
  resetIntegration
} from "../../services/integrationModule/integrationServices";

export default function COLLECTIONSTab() {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [integrationOptions, setIntegrationOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const softtype = "COLLECTIONS";

  // Load Applications and set selected
  const loadApplications = async () => {
    try {
      const res = await getIntegration(softtype);
      setApplications(res || []);
      const selected = res.find((app) => app.isSelected);
      setSelectedApp(selected ? selected.Id : null);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  useEffect(() => {
    loadApplications();
  }, []);

  // Fetch integration options when app changes
  useEffect(() => {
    if (selectedApp === null) {
      setIntegrationOptions([]);
      return;
    }

    const fetchOptions = async () => {
      try {
        const res = await getIntegrationOptions(selectedApp);
        setIntegrationOptions(res || []);
      } catch (error) {
        console.error("Error fetching integration options:", error);
      }
    };
    fetchOptions();
  }, [selectedApp]);

  // Handle checkbox toggle
  const handleCheckboxChange = (index, field) => {
    setIntegrationOptions((prev) =>
      prev.map((row, i) => {
        if (i === index) {
          // Force 0 if dp* is false
          if (
            (field === "enRead" && !row.dpRead) ||
            (field === "enCreate" && !row.dpCreate) ||
            (field === "enEdit" && !row.dpEdit) ||
            (field === "enDelete" && !row.dpDelete)
          ) {
            return { ...row, [field]: 0 };
          }
          return { ...row, [field]: !row[field] };
        }
        return row;
      })
    );
  };

  // Save integration options
  const handleSave = async () => {
    if (!selectedApp) return;
    setLoading(true);
    try {
      for (const row of integrationOptions) {
        await updateIntegrationOptions({
          id: row.Id,
          enRead: row.dpRead ? (row.enRead ? 1 : 0) : 0,
          enCreate: row.dpCreate ? (row.enCreate ? 1 : 0) : 0,
          enEdit: row.dpEdit ? (row.enEdit ? 1 : 0) : 0,
          enDelete: row.dpDelete ? (row.enDelete ? 1 : 0) : 0,
          softid: row.integrationsoftid,
          softtype
        });
      }
      alert("Integration options updated successfully.");
      await loadApplications();
    } catch (err) {
      console.error(err);
      alert("Error saving integration options.");
    } finally {
      setLoading(false);
    }
  };

  // Reset CRM integrations
  const handleReset = async () => {
    setLoading(true);
    try {
      await resetIntegration({ softtype });
      await loadApplications();
      setIntegrationOptions([]);
      setSelectedApp(null);
      alert("CRM integrations have been reset.");
    } catch (err) {
      console.error(err);
      alert("Error resetting CRM integrations.");
    } finally {
      setLoading(false);
    }
  };

  // Handle select/deselect
  const handleAppSelect = (id) => {
    if (selectedApp === id) {
      setSelectedApp(null); // allow deselect
    } else {
      setSelectedApp(id);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Dropdown + Save + Reset */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Application</InputLabel>
          <Select
            value={selectedApp ?? ""}
            label="Application"
            onChange={(e) => handleAppSelect(Number(e.target.value))}
            sx={{
                height: 36, // same as MUI button default
                "& .MuiSelect-select": {
                  paddingY: 0.5 // reduces vertical padding inside
                }
              }}
          >
            {applications.map((app) => (
              <MenuItem key={app.Id} value={app.Id}>
                {app.softwarename}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          sx={{ marginLeft: 2 }}
          variant="contained"
          onClick={handleSave}
          disabled={loading || !selectedApp}
        >
          Save
        </Button>
        <Button
          sx={{ marginLeft: 1 }}
          variant="outlined"
          color="secondary"
          onClick={handleReset}
          disabled={loading}
        >
          Reset All
        </Button>
      </Box>

      {/* Integration Options Table */}
      {integrationOptions.length > 0 && (
        <Table size="small" sx={{ "& td, & th": { py: 0.5 } }}>
          <TableHead sx={{ "& th": { fontWeight: "bold" } }}>
            <TableRow>
              <TableCell>Feature</TableCell>
              <TableCell>Read</TableCell>
              <TableCell>Create</TableCell>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {integrationOptions.map((option, index) => (
              <TableRow key={option.Id}>
                <TableCell>{option.integrationtype}</TableCell>
                {[
                  { field: "enRead", dp: "dpRead" },
                  { field: "enCreate", dp: "dpCreate" },
                  { field: "enEdit", dp: "dpEdit" },
                  { field: "enDelete", dp: "dpDelete" }
                ].map(({ field, dp }) => (
                  <TableCell key={field}>
                    <Checkbox
                      checked={!!option[field]}
                      onChange={() => handleCheckboxChange(index, field)}
                      disabled={!option[dp]}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
